import { Autocomplete, TextField } from "@mui/material";
import {
  AutocompleteScrollListbox,
  autocompleteScrollPaperProps,
} from "components/common/AutocompleteScrollListbox";
import {
  ClientItem,
  useGetIntegrationClientsQuery,
} from "features/integrationsBeta/registration";
import { useEffect, useMemo, useState } from "react";
import {
  blurActiveElement,
  FILTER_FIELD_H,
  FILTER_FIELD_W,
} from "./DateRangeSelect";

/**
 * Server-searchable Client filter cell (fixed 220x40). Emits the client id
 * for the monitor endpoint's ``client`` query param.
 */
export default function ClientFilterAutocomplete({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (clientId: string | undefined) => void;
}) {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<ClientItem | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(handle);
  }, [input]);

  const { data: clients = [], isFetching } = useGetIntegrationClientsQuery({
    search: search || undefined,
    page_size: 50,
  });

  // Clear Filters resets the external value - drop the local pick with it.
  useEffect(() => {
    if (value == null) setPicked(null);
  }, [value]);

  // Keep the selected value visible even when the search no longer matches.
  const options = useMemo(() => {
    if (picked && !clients.some((c) => c.id === picked.id)) {
      return [picked, ...clients];
    }
    return clients;
  }, [clients, picked]);

  return (
    <Autocomplete<ClientItem>
      options={options}
      value={picked}
      onChange={(_, next) => {
        setPicked(next);
        onChange(next ? String(next.id) : undefined);
      }}
      inputValue={input}
      onInputChange={(_, text) => setInput(text)}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={isFetching}
      blurOnSelect
      onClose={blurActiveElement}
      ListboxComponent={AutocompleteScrollListbox}
      componentsProps={autocompleteScrollPaperProps}
      sx={{ width: FILTER_FIELD_W }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Client"
          sx={{ "& .MuiInputBase-root": { height: FILTER_FIELD_H } }}
        />
      )}
    />
  );
}
